import React from 'react';
import { usePagination } from '@/hooks/usePagination';
import Pagination from './Pagination';
import { PageSizeSelector } from './PageSizeSelector';
import { PaginationInfo } from './PaginationInfo';
import { classNames } from '@/utils/classNames';

interface PaginatedTableProps<T> {
  data: T[];
  columns: Array<{
    key: string;
    label: string;
    className?: string;
    render?: (item: T, index: number) => React.ReactNode;
  }>;
  rowKey: string | ((item: T, index: number) => string | number);
  defaultPageSize?: number;
  storageKey?: string;
  className?: string;
  tableClassName?: string;
  showPageSizeSelector?: boolean;
  showPaginationInfo?: boolean;
  emptyMessage?: string;
  loading?: boolean;
  onRowClick?: (item: T, index: number) => void;
  getRowTestId?: (item: T, index: number) => string;
}

export function PaginatedTable<T = any>({
  data,
  columns,
  rowKey,
  defaultPageSize = 10,
  storageKey,
  className,
  tableClassName,
  showPageSizeSelector = true,
  showPaginationInfo = true,
  emptyMessage = 'データがありません',
  loading = false,
  onRowClick,
  getRowTestId,
}: PaginatedTableProps<T>) {
  const pagination = usePagination({
    data,
    defaultPageSize,
    storageKey,
  });

  const {
    currentPage,
    totalPages,
    pageSize,
    paginatedData,
    totalItems,
    startIndex,
    endIndex,
    setCurrentPage,
    setPageSize,
  } = pagination;

  // rowKeyが文字列の場合、関数に変換
  const getRowKey = typeof rowKey === 'string' ? (item: T) => (item as any)[rowKey] : rowKey;

  if (loading) {
    return (
      <div className={classNames('space-y-4', className)}>
        <div className='animate-pulse'>
          <div className='h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4'></div>
          {Array.from({ length: pageSize }).map((_, i) => (
            <div key={i} className='h-12 bg-gray-100 dark:bg-gray-800 rounded mb-2'></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('space-y-4', className)}>
      {/* ページサイズセレクターと情報表示 */}
      {(showPageSizeSelector || showPaginationInfo) && totalItems > 0 && (
        <div className='flex items-center justify-between flex-wrap gap-4'>
          {showPageSizeSelector && (
            <PageSizeSelector
              pageSize={pageSize}
              totalItems={totalItems}
              onPageSizeChange={setPageSize}
            />
          )}
          {showPaginationInfo && (
            <PaginationInfo
              currentPage={currentPage}
              totalPages={totalPages}
              startIndex={startIndex}
              endIndex={endIndex}
              totalItems={totalItems}
            />
          )}
        </div>
      )}

      {/* テーブル */}
      {totalItems === 0 ? (
        <div className='text-center py-12 text-gray-500 dark:text-gray-400'>{emptyMessage}</div>
      ) : (
        <div className='overflow-x-auto'>
          <table
            className={classNames(
              'min-w-full divide-y divide-gray-200 dark:divide-gray-700',
              tableClassName
            )}
          >
            <thead className='bg-gray-50 dark:bg-gray-800'>
              <tr>
                {columns.map(column => (
                  <th
                    key={column.key}
                    className={classNames(
                      'px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider',
                      column.className
                    )}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className='bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700'>
              {paginatedData.map((item, index) => (
                <tr
                  key={getRowKey(item, index)}
                  data-testid={getRowTestId ? getRowTestId(item, index) : undefined}
                  className={classNames(
                    'hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors',
                    onRowClick && 'cursor-pointer'
                  )}
                  onClick={onRowClick ? () => onRowClick(item, index) : undefined}
                >
                  {columns.map(column => (
                    <td
                      key={column.key}
                      className={classNames(
                        'px-6 py-4 whitespace-nowrap text-sm',
                        column.className
                      )}
                    >
                      {column.render
                        ? column.render(item, index)
                        : (item as any)[column.key] || '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className='flex justify-center'>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            showPrevNext={true}
            showFirstLast={true}
            maxVisible={5}
          />
        </div>
      )}
    </div>
  );
}
